import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, DollarSign, TrendingUp, Clock, Edit } from 'lucide-react';
import { toast } from 'sonner';

const Financeiro = () => {
  const [financeiro, setFinanceiro] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFinanceiro, setEditingFinanceiro] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [formData, setFormData] = useState({
    processo_id: '',
    cliente_id: '',
    descricao: '',
    valor_total: '',
    valor_pago: '0',
    numero_parcelas: '1',
    parcela_atual: '0',
    data_vencimento: '',
    data_pagamento: '',
    status: 'Pendente',
    observacoes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [financeiroRes, clientesRes, processosRes] = await Promise.all([
        api.getFinanceiro(),
        api.getClientes(),
        api.getProcessos()
      ]);
      setFinanceiro(financeiroRes.data);
      setClientes(clientesRes.data);
      setProcessos(processosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        valor_total: parseFloat(formData.valor_total),
        valor_pago: parseFloat(formData.valor_pago),
        numero_parcelas: parseInt(formData.numero_parcelas),
        parcela_atual: parseInt(formData.parcela_atual),
        data_vencimento: formData.data_vencimento || null,
        data_pagamento: formData.data_pagamento || null
      };
      
      if (editingFinanceiro) {
        await api.updateFinanceiro(editingFinanceiro.id, submitData);
        toast.success('Registro atualizado com sucesso!');
      } else {
        await api.createFinanceiro(submitData);
        toast.success('Registro financeiro criado com sucesso!');
      }
      
      setIsDialogOpen(false);
      setEditingFinanceiro(null);
      setFormData({
        processo_id: '',
        cliente_id: '',
        descricao: '',
        valor_total: '',
        valor_pago: '0',
        numero_parcelas: '1',
        parcela_atual: '0',
        data_vencimento: '',
        data_pagamento: '',
        status: 'Pendente',
        observacoes: ''
      });
      loadData();
    } catch (error) {
      console.error('Erro ao salvar registro:', error);
      toast.error('Erro ao salvar registro financeiro');
    }
  };

  const handleEdit = (item) => {
    setEditingFinanceiro(item);
    setFormData({
      processo_id: item.processo_id,
      cliente_id: item.cliente_id,
      descricao: item.descricao,
      valor_total: item.valor_total.toString(),
      valor_pago: item.valor_pago.toString(),
      numero_parcelas: item.numero_parcelas.toString(),
      parcela_atual: item.parcela_atual.toString(),
      data_vencimento: item.data_vencimento ? item.data_vencimento.split('T')[0] : '',
      data_pagamento: item.data_pagamento ? item.data_pagamento.split('T')[0] : '',
      status: item.status,
      observacoes: item.observacoes || ''
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingFinanceiro(null);
    setFormData({
      processo_id: '',
      cliente_id: '',
      descricao: '',
      valor_total: '',
      valor_pago: '0',
      numero_parcelas: '1',
      parcela_atual: '0',
      data_vencimento: '',
      data_pagamento: '',
      status: 'Pendente',
      observacoes: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'bg-amber-100 text-amber-700',
      'Pago': 'bg-green-100 text-green-700',
      'Atrasado': 'bg-red-100 text-red-700',
      'Cancelado': 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const filteredFinanceiro = filter === 'Todos' 
    ? financeiro 
    : financeiro.filter(f => f.status === filter);

  const totalRecebido = financeiro
    .filter(f => f.status === 'Pago')
    .reduce((sum, f) => sum + f.valor_pago, 0);

  const totalPendente = financeiro
    .filter(f => f.status === 'Pendente')
    .reduce((sum, f) => sum + (f.valor_total - f.valor_pago), 0);

  if (loading) {
    return <div className="p-8" data-testid="financeiro-loading">Carregando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="financeiro-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Financeiro</h1>
          <p className="text-slate-600 mt-1">Controle de honorários e pagamentos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-financeiro-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Registro
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingFinanceiro ? 'Editar Registro Financeiro' : 'Novo Registro Financeiro'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cliente_id">Cliente *</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="financeiro-cliente-select">
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nome_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="processo_id">Processo *</Label>
                  <Select
                    value={formData.processo_id}
                    onValueChange={(value) => setFormData({ ...formData, processo_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="financeiro-processo-select">
                      <SelectValue placeholder="Selecione um processo" />
                    </SelectTrigger>
                    <SelectContent>
                      {processos.map((processo) => (
                        <SelectItem key={processo.id} value={processo.id}>
                          {processo.numero_processo} - {processo.tipo_acao}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="descricao">Descrição *</Label>
                  <Input
                    id="descricao"
                    data-testid="financeiro-descricao-input"
                    required
                    placeholder="Ex: Honorários - Ação de Cobrança"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valor_total">Valor Total *</Label>
                    <Input
                      id="valor_total"
                      data-testid="financeiro-valor-input"
                      type="number"
                      step="0.01"
                      required
                      placeholder="0.00"
                      value={formData.valor_total}
                      onChange={(e) => setFormData({ ...formData, valor_total: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="valor_pago">Valor Pago</Label>
                    <Input
                      id="valor_pago"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.valor_pago}
                      onChange={(e) => setFormData({ ...formData, valor_pago: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="numero_parcelas">Número de Parcelas</Label>
                    <Input
                      id="numero_parcelas"
                      type="number"
                      min="1"
                      value={formData.numero_parcelas}
                      onChange={(e) => setFormData({ ...formData, numero_parcelas: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="parcela_atual">Parcela Atual</Label>
                    <Input
                      id="parcela_atual"
                      type="number"
                      min="0"
                      value={formData.parcela_atual}
                      onChange={(e) => setFormData({ ...formData, parcela_atual: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data_vencimento">Data de Vencimento</Label>
                    <Input
                      id="data_vencimento"
                      type="date"
                      value={formData.data_vencimento}
                      onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_pagamento">Data de Pagamento</Label>
                    <Input
                      id="data_pagamento"
                      type="date"
                      value={formData.data_pagamento}
                      onChange={(e) => setFormData({ ...formData, data_pagamento: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                      <SelectItem value="Pago">Pago</SelectItem>
                      <SelectItem value="Atrasado">Atrasado</SelectItem>
                      <SelectItem value="Cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <textarea
                    id="observacoes"
                    className="w-full min-h-[100px] p-2 border rounded-md"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-financeiro-button">
                  {editingFinanceiro ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card data-testid="financeiro-stat-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total</CardTitle>
            <DollarSign className="w-5 h-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalRecebido + totalPendente)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Recebido</CardTitle>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRecebido)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Pendente</CardTitle>
            <Clock className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{formatCurrency(totalPendente)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        {['Todos', 'Pendente', 'Pago', 'Atrasado'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            data-testid={`filter-${status.toLowerCase()}-button`}
          >
            {status}
          </Button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredFinanceiro.length > 0 ? (
          filteredFinanceiro.map((item) => (
            <Card key={item.id} className="hover:shadow-lg transition-shadow" data-testid="financeiro-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{item.descricao}</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">
                      Cliente: {item.cliente?.nome_completo}
                    </p>
                    <p className="text-sm text-slate-500">
                      Processo: {item.processo?.numero_processo}
                    </p>
                  </div>
                  <div className="flex gap-2 items-start">
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      data-testid="edit-financeiro-button"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Valor Total</p>
                    <p className="font-semibold text-slate-900">{formatCurrency(item.valor_total)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Valor Pago</p>
                    <p className="font-semibold text-green-600">{formatCurrency(item.valor_pago)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Valor Pendente</p>
                    <p className="font-semibold text-amber-600">{formatCurrency(item.valor_total - item.valor_pago)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Parcelas</p>
                    <p className="font-semibold text-slate-900">{item.parcela_atual}/{item.numero_parcelas}</p>
                  </div>
                </div>
                {item.data_vencimento && (
                  <p className="text-sm text-slate-500 mt-2">
                    Vencimento: {new Date(item.data_vencimento).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-12 text-slate-500">
            Nenhum registro encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default Financeiro;